/**
 * FirebaseCache - Sistema de caché para optimizar llamadas a Firebase
 * Usa LocalStorage únicamente con TTL(time to live) configurable
 */

// Constante global para configurar los minutos de caché
const CACHE_SECONDS = 300 ;

class FirebaseCache {
    constructor() {
      this.TTL = CACHE_SECONDS * 1000; // Convertir minutos a milisegundos
      this.localStoragePrefix = 'firebase_cache_';
      
    }
  
    /**
     * Obtener datos con caché (SOLO LOCALSTORAGE)
     * @param {string} key - Clave del caché
     * @param {Function} fetcher - Función que obtiene los datos de Firebase
     * @returns {Promise} - Datos del caché o de Firebase
     */
    async get(key, fetcher) {
      const storageKey = this.localStoragePrefix + key;
      
      try {
        // Intentar obtener de localStorage
        const stored = localStorage.getItem(storageKey);
        
        if (stored) {
          const parsed = JSON.parse(stored);
          const now = Date.now();
          
          // Verificar si no ha expirado
          if (parsed.timestamp && (now - parsed.timestamp) < this.TTL) {
            return parsed.data;
          } else {
            localStorage.removeItem(storageKey);
          }
        }
        
        // Cache MISS - Obtener de Firebase
        const data = await fetcher();
        
        // Guardar en localStorage
        this.set(key, data);
        
        return data;
        
      } catch (error) {
        console.warn(`❌ Cache error for ${key}:`, error);
        // Si hay error, ir directo a Firebase
        return await fetcher();
      }
    }
  
    /**
     * Guardar en localStorage
     * @param {string} key - Clave del caché
     * @param {*} data - Datos a guardar
     */
    set(key, data) {
      const storageKey = this.localStoragePrefix + key;
      
      try {
        const toStore = {
          data: data,
          timestamp: Date.now(),
          ttl: this.TTL
        };
        
        localStorage.setItem(storageKey, JSON.stringify(toStore));
        
      } catch (error) {
        console.warn('❌ Error saving to localStorage:', error);
        // Si localStorage está lleno, limpiar caché antiguo
        this.cleanOldLocalStorageItems();
        
        // Intentar guardar de nuevo
        try {
          const toStore = {
            data: data,
            timestamp: Date.now(),
            ttl: this.TTL
          };
          localStorage.setItem(storageKey, JSON.stringify(toStore));
        } catch (retryError) {
          console.warn('❌ Failed to save after cleanup:', retryError);
        }
      }
    }
  
    /**
     * Invalidar caché específico (SOLO LOCALSTORAGE)
     * @param {string} key - Clave del caché a invalidar
     */
    invalidate(key) {
      const storageKey = this.localStoragePrefix + key;
      
      try {
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.warn(`❌ Error invalidating cache for ${key}:`, error);
      }
    }
  
    /**
     * Invalidar por patrón (SOLO LOCALSTORAGE)
     * @param {string} pattern - Patrón regex para invalidar múltiples claves
     */
    invalidatePattern(pattern) {
      try {
        const regex = new RegExp(pattern);
        const keys = Object.keys(localStorage);
        
        keys.forEach(storageKey => {
          if (storageKey.startsWith(this.localStoragePrefix)) {
            const cacheKey = storageKey.replace(this.localStoragePrefix, '');
            if (regex.test(cacheKey)) {
              localStorage.removeItem(storageKey);
            }
          }
        });
        
      } catch (error) {
        console.warn(`❌ Error invalidating pattern ${pattern}:`, error);
      }
    }
  }
  
  // Instancia global del caché (SOLO LOCALSTORAGE) - Usa la constante CACHE_SECONDS
  export const firebaseCache = new FirebaseCache();
  
  // Exportar también la clase para testing
  export default FirebaseCache;